import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import RPNInput, {
  getCountryCallingCode,
  isValidPhoneNumber,
  parsePhoneNumber,
} from 'react-phone-number-input'
import type { Country, Value as E164Value } from 'react-phone-number-input'
import { getExampleNumber } from 'libphonenumber-js'
import mobileExamples from 'libphonenumber-js/examples.mobile.json'
import classNames from 'classnames'

import 'react-phone-number-input/style.css'

import CountrySelect from './CountrySelect'
import styles from './PhoneInput.module.scss'

// Build a masked placeholder like "(XXX) XXX-XXXX" for the selected country,
// matching the visual cue the legacy intl-tel-input setup provided via
// `customPlaceholder`.
const maskedPlaceholderFor = (country: Country): string => {
  const example = getExampleNumber(country, mobileExamples)
  return example ? example.formatNational().replace(/\d/g, 'X') : ''
}

export type PhoneInputChange = {
  /**
   * National phone number in human-readable format, e.g. "(415) 555-5555".
   * This is what callers store as the user-facing display value and what
   * downstream analytics / booking payloads see.
   */
  national: string
  /** Raw national digits with no formatting, e.g. "4155555555". */
  nationalDigits: string
  /** Full E.164 number, e.g. "+14155555555". */
  e164: string
  /** Country calling code without the leading `+`, e.g. "1". */
  dialCode: string
  countryCode: Country | undefined
  isValid: boolean
}

export type PhoneInputProps = {
  /**
   * The display value controlled by the caller. The wrapper uses E.164 as its
   * internal source of truth; this prop is read only to detect caller-driven
   * resets (e.g. country switched, form reset to empty).
   */
  value?: string
  onChange: (change: PhoneInputChange) => void
  onBlur?: () => void
  onCountryChange?: (country?: Country) => void
  defaultCountry?: Country
  id?: string
  name?: string
  ariaLabel?: string
  autoComplete?: string
  placeholder?: string
  containerClassName?: string
  inputClassName?: string
  hasError?: boolean
  disabled?: boolean
  tabIndex?: number
  maxLength?: number
  inputRef?: React.Ref<HTMLInputElement>
}

/**
 * Pure helper that normalizes an E.164 phone string into the change payload
 * this wrapper emits. Exported so we can unit-test NANP and international
 * validation behavior without having to drive the full RPNI DOM component.
 *
 * Accepts a plain `string` (not `E164Value`) so tests and consumers don't have
 * to cast — runtime validation happens via libphonenumber-js anyway.
 */
export const describePhoneNumber = (
  e164: string | undefined,
  fallbackCountry: Country,
): PhoneInputChange => {
  if (!e164) {
    return {
      national: '',
      nationalDigits: '',
      e164: '',
      dialCode: getCountryCallingCode(fallbackCountry),
      countryCode: fallbackCountry,
      isValid: false,
    }
  }
  const parsed = parsePhoneNumber(e164)
  if (!parsed) {
    // Unparseable E.164 — treat as no value so downstream hashing/formatting
    // never sees the raw E.164 string leak into the `national`/`nationalDigits`
    // fields, which would double-count the country code when a caller later
    // prepends `dialCode`.
    return {
      national: '',
      nationalDigits: '',
      e164,
      dialCode: getCountryCallingCode(fallbackCountry),
      countryCode: fallbackCountry,
      isValid: false,
    }
  }
  return {
    national: parsed.formatNational(),
    // `nationalNumber` is a digit-only string in every current libphonenumber-js
    // version, but defend against future drift by stripping non-digits.
    nationalDigits: String(parsed.nationalNumber).replace(/\D+/g, ''),
    e164,
    dialCode: String(parsed.countryCallingCode),
    countryCode: parsed.country,
    isValid: isValidPhoneNumber(e164),
  }
}

const PhoneInput: React.FC<PhoneInputProps> = ({
  value,
  onChange,
  onBlur,
  onCountryChange,
  defaultCountry = 'US',
  id,
  name,
  ariaLabel,
  autoComplete = 'tel',
  placeholder,
  containerClassName,
  inputClassName,
  hasError,
  disabled,
  tabIndex,
  maxLength,
  inputRef,
}) => {
  // Rehydrate E.164 from the caller's `value` once, at mount. Runtime typing
  // updates come from RPNI's onChange through `handleChange` — we do NOT run
  // an effect on every `value` change, because that causes a parse-and-
  // setState churn on each keystroke that visibly lags the input.
  const [e164, setE164] = useState<E164Value | undefined>(() => {
    if (!value) return undefined
    const parsed = parsePhoneNumber(value, defaultCountry)
    return parsed?.number as E164Value | undefined
  })
  const [activeCountry, setActiveCountry] = useState<Country>(() => {
    if (!value) return defaultCountry
    return parsePhoneNumber(value, defaultCountry)?.country ?? defaultCountry
  })
  const countryRef = useRef<Country>(activeCountry)

  // Re-sync tracking when the caller swaps `defaultCountry` (rerender flow).
  // Without this the placeholder and fallback dial code would stay locked to
  // the mount-time country.
  useEffect(() => {
    countryRef.current = defaultCountry
    setActiveCountry(defaultCountry)
  }, [defaultCountry])

  // Regenerate the country-specific masked placeholder only when the active
  // country changes. Keeps the computed value stable across re-renders.
  const resolvedPlaceholder = useMemo(
    () => placeholder ?? maskedPlaceholderFor(activeCountry),
    [placeholder, activeCountry],
  )

  // Only externally-driven clears (form reset, caller setting value back to
  // '') need to flow into internal state. Any other `value` change during
  // typing is an echo of our own emission and is ignored.
  useEffect(() => {
    if (value === '' && e164) {
      setE164(undefined)
    }
  }, [value, e164])

  const handleChange = useCallback(
    (next: E164Value | undefined) => {
      // Deliberately NOT calling setE164 here. RPNI owns its own phoneDigits
      // state for typing — mirroring into our own e164 state on every
      // keystroke added a second render per keypress and dropped characters
      // under fast typing (the controlled-input + React batching pattern
      // where the formatted display can overwrite in-flight keystrokes).
      // `e164` is only re-seeded on mount (lazy initializer above) and on
      // external reset (`value === ''` effect below).
      const change = describePhoneNumber(next, countryRef.current)
      if (change.countryCode && change.countryCode !== countryRef.current) {
        countryRef.current = change.countryCode
        setActiveCountry(change.countryCode)
      }
      onChange(change)
    },
    [onChange],
  )

  const handleCountryChange = useCallback(
    (country?: Country) => {
      if (country && country !== countryRef.current) {
        countryRef.current = country
        setActiveCountry(country)
      }
      onCountryChange?.(country)
    },
    [onCountryChange],
  )

  return (
    <RPNInput
      addInternationalOption={false}
      autoComplete={autoComplete}
      className={classNames(
        styles.phoneInput,
        { [styles.phoneInputError]: hasError },
        containerClassName,
      )}
      countrySelectComponent={CountrySelect}
      defaultCountry={defaultCountry}
      disabled={disabled}
      // Disable the library's post-country-change auto-focus. RPNI's
      // onCountryChange handler dereferences `this.inputRef.current.focus()`
      // without a null guard (see
      // node_modules/react-phone-number-input/modules/PhoneInputWithCountry.js
      // line ~77), which crashed in the wild when the ref hadn't attached yet
      // on first interaction. Skipping the jump-to-input is a minor UX cost
      // and avoids a hard error we can't fix without forking the library.
      focusInputOnCountrySelection={false}
      inputRef={inputRef}
      // Force NATIONAL-format display: the country calling code renders in our
      // CountrySelect prefix, not inside the <input>. This also matches the
      // legacy intl-tel-input UX where users type only national digits.
      international={false}
      // Cap typing at the maximum length valid for the selected country —
      // preserves the digit-cap guard rail the legacy intl-tel-input setup
      // enforced via telInputProps.maxLength.
      limitMaxLength
      numberInputProps={{
        'aria-invalid': hasError || undefined,
        'aria-label': ariaLabel,
        'className': classNames(styles.numberInput, inputClassName),
        'id': id,
        maxLength,
        'name': name,
        'placeholder': resolvedPlaceholder,
        'tabIndex': tabIndex,
      }}
      onBlur={onBlur}
      onChange={handleChange}
      onCountryChange={handleCountryChange}
      value={e164}
    />
  )
}

export default PhoneInput
